import {Select} from '@mantine/core';
import {type FC, useState} from 'react';
import {MdArrowForward} from 'react-icons/md';
import {PiCaretUpDownBold} from 'react-icons/pi';
import {useInView} from 'react-intersection-observer';
import {
  type RankingMetric,
  useRankedColumnList,
} from '../../../../../../core/repositories/columnRepository';
import {cn} from '../../../../../../lib/classname';
import {CommonLink} from '../../../../../../shared/components/CommonLink';
import {Card, CardSkeleton} from '../../Card';
import {Carousel} from '../../Carousel';
import {Title} from '../../Carousel/Title';
import {RANKING_METRIC_OPTIONS} from '../../constants';
import {COLUMNS_RANKING} from '../../routes';

type Props = {
  className?: string;
};

const LIMIT = 10;

export const RankingCarousel: FC<Props> = ({className}) => {
  const [metric, setMetric] = useState<RankingMetric>('daily_pv');
  const {ref, inView} = useInView({
    triggerOnce: true,
  });
  const {data, isLoading} = useRankedColumnList({
    section: metric,
    limit: LIMIT,
    config: {
      enabled: inView,
    },
  });
  const posts = data?.ok ? data.value.posts : [];
  const detailLink = COLUMNS_RANKING(metric);

  return (
    <div className={cn('w-full odd:bg-white py-16', className)} ref={ref}>
      <div className="flex justify-between gap-8 mx-16 pc:mx-[max(calc((100vw-1200px)/2),24px)] mb-16">
        <Title title="注目度ランキング" detailLink={detailLink} />
        <div>
          <Select
            data={RANKING_METRIC_OPTIONS}
            value={metric}
            onChange={(value) => {
              if (!value) return;
              setMetric(value as RankingMetric);
            }}
            classNames={{
              wrapper: 'wrapper',
              root: 'root',
              item: 'item text-center',
              input:
                'text-primary font-bold text-base bg-transparent border-none w-[80px]',
            }}
            rightSection={
              <PiCaretUpDownBold size="1rem" className="text-primary" />
            }
          />
        </div>
      </div>

      <Carousel className="max-w-[1200px] ml-16 pc:mx-auto">
        <div
          className={cn('flex flex-nowrap gap-16 mr-16', 'pc:mx-0 pc:gap-24')}
        >
          <>
            {isLoading
              ? Array.from({length: 10}).map((_, index) => (
                  <CardSkeleton
                    // biome-ignore lint/suspicious/noArrayIndexKey: placeholder
                    key={index}
                    className="w-[80vw] pc:w-[calc(((1200px-24px*2)/3)-30px)]"
                    imageHeight={148}
                  />
                ))
              : posts.map((post) => (
                  <Card
                    key={post.id}
                    id={post.id}
                    title={post.title}
                    clipCount={post.favoriteCount}
                    isClipped={post.favorited}
                    tagNames={post.tags}
                    thumbnail={post.cardImageURL ?? post.carouselThumbnailURL}
                    className="w-[80vw] pc:w-[calc(((1200px-24px*2)/3)-30px)]"
                    sponsoredOption={
                      post.sponsorCompanies
                        ? {
                            enabled: true,
                            companies: post.sponsorCompanies,
                          }
                        : {enabled: false}
                    }
                  />
                ))}

            {posts.length === LIMIT && (
              <CommonLink
                href={detailLink}
                className={cn(
                  'flex items-center justify-center w-[200px] h-full bg-white border border-black-5 p-4 rounded-16 hover:opacity-60',
                )}
              >
                <div className="flex flex-col items-center">
                  <MdArrowForward size={32} />
                  <span className="text-[14px] text-black text-center mt-1">
                    もっと見る
                  </span>
                </div>
              </CommonLink>
            )}
          </>
        </div>
      </Carousel>
    </div>
  );
};
